import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';
import { statusToneInteractiveFieldClasses } from '../../lib/statusTone';

const successFieldClasses = statusToneInteractiveFieldClasses('success');
const warningFieldClasses = statusToneInteractiveFieldClasses('warning');
const errorFieldClasses = statusToneInteractiveFieldClasses('danger');

const inputVariants = cva(
  'flex w-full rounded-lg border bg-surface text-text-primary placeholder:text-text-tertiary shadow-sm transition-all duration-150 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-neutral-50',
  {
    variants: {
      variant: {
        default:
          'border-[var(--input-border-default)] hover:border-[var(--input-border-hover)] hover:shadow-md focus:border-[var(--input-border-focus)] focus:shadow-md focus:ring-2 focus:ring-brand-blue-500/30',
        error: cn('focus:ring-2 focus:shadow-md', ...errorFieldClasses),
        success: cn('focus:ring-2 focus:shadow-md', ...successFieldClasses),
        warning: cn('focus:ring-2 focus:shadow-md', ...warningFieldClasses),
      },
      size: {
        sm: 'h-8 px-2.5 py-1.5 text-xs',
        md: 'h-10 px-3.5 py-2 text-sm',
        lg: 'h-12 px-4 py-3 text-base',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  }
);

interface InputProps
  extends
    Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof inputVariants> {
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  error?: string;
  helperText?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, variant, size, startIcon, endIcon, error, helperText, type, ...props }, ref) => {
    const hasError = !!error;
    const effectiveVariant = hasError ? 'error' : variant;

    const inputElement = (
      <input
        type={type}
        className={cn(
          inputVariants({ variant: effectiveVariant, size }),
          startIcon && 'pl-9',
          endIcon && 'pr-9',
          className
        )}
        ref={ref}
        aria-invalid={hasError}
        aria-describedby={
          error ? `${props.id}-error` : helperText ? `${props.id}-helper` : undefined
        }
        {...props}
      />
    );

    if (startIcon || endIcon) {
      return (
        <div className="relative w-full">
          {startIcon && (
            <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary transition-colors duration-150">
              {startIcon}
            </div>
          )}
          {inputElement}
          {endIcon && (
            <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary transition-colors duration-150">
              {endIcon}
            </div>
          )}
          {(error || helperText) && (
            <div className="mt-1 text-xs">
              {error && (
                <p id={`${props.id}-error`} className="text-error-500">
                  {error}
                </p>
              )}
              {!error && helperText && (
                <p id={`${props.id}-helper`} className="text-text-secondary">
                  {helperText}
                </p>
              )}
            </div>
          )}
        </div>
      );
    }

    return (
      <div>
        {inputElement}
        {(error || helperText) && (
          <div className="mt-1 text-xs">
            {error && (
              <p id={`${props.id}-error`} className="text-error-500">
                {error}
              </p>
            )}
            {!error && helperText && (
              <p id={`${props.id}-helper`} className="text-text-secondary">
                {helperText}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }
);
Input.displayName = 'Input';

export { Input, inputVariants };
